import React, { useState } from 'react';
import { SearchIcon, PlusIcon, FileTextIcon, CalendarIcon, CheckCircleIcon, ClockIcon, XIcon, AlertTriangleIcon, FileIcon } from 'lucide-react';
export function ContractAmendments({
  locale
}) {
  const [searchTerm, setSearchTerm] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [selectedAmendment, setSelectedAmendment] = useState(null);
  const [formData, setFormData] = useState({
    contractId: '',
    amendmentNumber: '',
    title: '',
    description: '',
    reason: '',
    requestDate: '',
    approvalDate: '',
    effectiveDate: '',
    status: 'Draft',
    documents: []
  });
  // Sample amendments data
  const amendments = [{
    id: 'AMD-2023-001',
    contractId: 'CTR-2023-002',
    contractName: 'IT Support Services',
    vendor: 'TechPro Solutions',
    amendmentNumber: '1',
    title: 'Service Level Agreement Update',
    description: 'Updated response time requirements for critical issues',
    reason: 'Operational requirements',
    requestDate: '2023-06-10',
    approvalDate: '2023-06-20',
    effectiveDate: '2023-07-01',
    status: 'Approved'
  }, {
    id: 'AMD-2023-002',
    contractId: 'CTR-2023-001',
    contractName: 'Office Lease Agreement',
    vendor: 'Skyline Properties Inc.',
    amendmentNumber: '1',
    title: 'Maintenance Responsibility Update',
    description: 'Clarification on maintenance responsibilities for common areas',
    reason: 'Dispute prevention',
    requestDate: '2023-05-15',
    approvalDate: '',
    effectiveDate: '',
    status: 'Pending Approval'
  }, {
    id: 'AMD-2023-003',
    contractId: 'CTR-2023-006',
    contractName: 'Consulting Services',
    vendor: 'Business Consultants Ltd.',
    amendmentNumber: '1',
    title: 'Payment Terms Modification',
    description: 'Changed payment schedule from monthly to quarterly',
    reason: 'Cash flow management',
    requestDate: '2023-07-05',
    approvalDate: '',
    effectiveDate: '',
    status: 'Draft'
  }, {
    id: 'AMD-2023-004',
    contractId: 'CTR-2023-010',
    contractName: 'Cloud Infrastructure',
    vendor: 'AWS',
    amendmentNumber: '1',
    title: 'Data Retention Policy Update',
    description: 'Updated data retention and backup requirements',
    reason: 'Compliance requirements',
    requestDate: '2023-06-25',
    approvalDate: '2023-07-10',
    effectiveDate: '2023-08-01',
    status: 'Approved'
  }, {
    id: 'AMD-2023-005',
    contractId: 'CTR-2023-008',
    contractName: 'Security Services',
    vendor: 'SecureGuard Inc.',
    amendmentNumber: '1',
    title: 'Personnel Background Check Requirements',
    description: 'Enhanced background check requirements for security personnel',
    reason: 'Risk management',
    requestDate: '2023-07-12',
    approvalDate: '',
    effectiveDate: '',
    status: 'Rejected'
  }];
  // Filter amendments based on search term
  const filteredAmendments = amendments.filter(amendment => amendment.id.toLowerCase().includes(searchTerm.toLowerCase()) || amendment.contractId.toLowerCase().includes(searchTerm.toLowerCase()) || amendment.contractName.toLowerCase().includes(searchTerm.toLowerCase()) || amendment.vendor.toLowerCase().includes(searchTerm.toLowerCase()) || amendment.title.toLowerCase().includes(searchTerm.toLowerCase()) || amendment.description.toLowerCase().includes(searchTerm.toLowerCase()));
  // Get status badge color
  const getStatusColor = status => {
    switch (status) {
      case 'Approved':
        return 'bg-green-100 text-green-800';
      case 'Pending Approval':
        return 'bg-yellow-100 text-yellow-800';
      case 'Draft':
        return 'bg-gray-100 text-gray-800';
      case 'Rejected':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };
  // Get status icon
  const getStatusIcon = status => {
    switch (status) {
      case 'Approved':
        return CheckCircleIcon;
      case 'Pending Approval':
        return ClockIcon;
      case 'Draft':
        return FileTextIcon;
      case 'Rejected':
        return XIcon;
      default:
        return AlertTriangleIcon;
    }
  };
  const handleChange = e => {
    const {
      name,
      value,
      type,
      checked
    } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };
  const handleSubmit = e => {
    e.preventDefault();
    // In a real app, this would save the amendment to the database
    console.log('Saving amendment:', formData);
    // Show success message
    alert(`Amendment saved successfully!`);
    // Reset form and view
    setShowForm(false);
    setSelectedAmendment(null);
  };
  const handleAddNew = () => {
    setFormData({
      contractId: '',
      amendmentNumber: '',
      title: '',
      description: '',
      reason: '',
      requestDate: '',
      approvalDate: '',
      effectiveDate: '',
      status: 'Draft',
      documents: []
    });
    setSelectedAmendment(null);
    setShowForm(true);
  };
  const handleEdit = amendment => {
    setFormData({
      contractId: amendment.contractId,
      amendmentNumber: amendment.amendmentNumber,
      title: amendment.title,
      description: amendment.description,
      reason: amendment.reason,
      requestDate: amendment.requestDate,
      approvalDate: amendment.approvalDate,
      effectiveDate: amendment.effectiveDate,
      status: amendment.status,
      documents: []
    });
    setSelectedAmendment(amendment);
    setShowForm(true);
  };
  return <div data-id="element-2025">
      <div className="mb-8 flex justify-between items-center" data-id="element-2026">
        <div data-id="element-2027">
          <h2 className="text-2xl font-bold text-gray-900" data-id="element-2028">
            Contract Amendments
          </h2>
          <p className="mt-1 text-sm text-gray-500" data-id="element-2029">
            Manage amendments to existing contracts
          </p>
        </div>
        <button onClick={handleAddNew} className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none" data-id="element-2030">
          <PlusIcon className="h-4 w-4 mr-2" data-id="element-2031" />
          Add Amendment
        </button>
      </div>
      {showForm ? <div className="bg-white shadow overflow-hidden sm:rounded-lg" data-id="element-2032">
          <form onSubmit={handleSubmit} className="p-6" data-id="element-2033">
            <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6" data-id="element-2034">
              {/* Contract Selection */}
              <div className="sm:col-span-3" data-id="element-2035">
                <label htmlFor="contractId" className="block text-sm font-medium text-gray-700" data-id="element-2036">
                  Contract
                </label>
                <div className="mt-1" data-id="element-2037">
                  <select id="contractId" name="contractId" value={formData.contractId} onChange={handleChange} required className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-2038">
                    <option value="" data-id="element-2039">Select a contract</option>
                    <option value="CTR-2023-001" data-id="element-2040">
                      CTR-2023-001 - Office Lease Agreement
                    </option>
                    <option value="CTR-2023-002" data-id="element-2041">
                      CTR-2023-002 - IT Support Services
                    </option>
                    <option value="CTR-2023-006" data-id="element-2042">
                      CTR-2023-006 - Consulting Services
                    </option>
                    <option value="CTR-2023-008" data-id="element-2043">
                      CTR-2023-008 - Security Services
                    </option>
                    <option value="CTR-2023-010" data-id="element-2044">
                      CTR-2023-010 - Cloud Infrastructure
                    </option>
                  </select>
                </div>
              </div>
              {/* Amendment Number */}
              <div className="sm:col-span-3" data-id="element-2045">
                <label htmlFor="amendmentNumber" className="block text-sm font-medium text-gray-700" data-id="element-2046">
                  Amendment Number
                </label>
                <div className="mt-1" data-id="element-2047">
                  <input type="text" name="amendmentNumber" id="amendmentNumber" value={formData.amendmentNumber} onChange={handleChange} required className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-2048" />
                </div>
              </div>
              {/* Title */}
              <div className="sm:col-span-6" data-id="element-2049">
                <label htmlFor="title" className="block text-sm font-medium text-gray-700" data-id="element-2050">
                  Amendment Title
                </label>
                <div className="mt-1" data-id="element-2051">
                  <input type="text" name="title" id="title" value={formData.title} onChange={handleChange} required className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-2052" />
                </div>
              </div>
              {/* Description */}
              <div className="sm:col-span-6" data-id="element-2053">
                <label htmlFor="description" className="block text-sm font-medium text-gray-700" data-id="element-2054">
                  Description
                </label>
                <div className="mt-1" data-id="element-2055">
                  <textarea id="description" name="description" rows={3} value={formData.description} onChange={handleChange} required className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-2056" />
                </div>
              </div>
              {/* Reason */}
              <div className="sm:col-span-6" data-id="element-2057">
                <label htmlFor="reason" className="block text-sm font-medium text-gray-700" data-id="element-2058">
                  Reason for Amendment
                </label>
                <div className="mt-1" data-id="element-2059">
                  <textarea id="reason" name="reason" rows={2} value={formData.reason} onChange={handleChange} required className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-2060" />
                </div>
              </div>
              {/* Request Date */}
              <div className="sm:col-span-2" data-id="element-2061">
                <label htmlFor="requestDate" className="block text-sm font-medium text-gray-700" data-id="element-2062">
                  Request Date
                </label>
                <div className="mt-1" data-id="element-2063">
                  <input type="date" name="requestDate" id="requestDate" value={formData.requestDate} onChange={handleChange} required className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-2064" />
                </div>
              </div>
              {/* Approval Date */}
              <div className="sm:col-span-2" data-id="element-2065">
                <label htmlFor="approvalDate" className="block text-sm font-medium text-gray-700" data-id="element-2066">
                  Approval Date
                </label>
                <div className="mt-1" data-id="element-2067">
                  <input type="date" name="approvalDate" id="approvalDate" value={formData.approvalDate} onChange={handleChange} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-2068" />
                </div>
              </div>
              {/* Effective Date */}
              <div className="sm:col-span-2" data-id="element-2069">
                <label htmlFor="effectiveDate" className="block text-sm font-medium text-gray-700" data-id="element-2070">
                  Effective Date
                </label>
                <div className="mt-1" data-id="element-2071">
                  <input type="date" name="effectiveDate" id="effectiveDate" value={formData.effectiveDate} onChange={handleChange} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-2072" />
                </div>
              </div>
              {/* Status */}
              <div className="sm:col-span-3" data-id="element-2073">
                <label htmlFor="status" className="block text-sm font-medium text-gray-700" data-id="element-2074">
                  Status
                </label>
                <div className="mt-1" data-id="element-2075">
                  <select id="status" name="status" value={formData.status} onChange={handleChange} required className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-2076">
                    <option value="Draft" data-id="element-2077">Draft</option>
                    <option value="Pending Approval" data-id="element-2078">Pending Approval</option>
                    <option value="Approved" data-id="element-2079">Approved</option>
                    <option value="Rejected" data-id="element-2080">Rejected</option>
                  </select>
                </div>
              </div>
              {/* Document Upload */}
              <div className="sm:col-span-6" data-id="element-2081">
                <label className="block text-sm font-medium text-gray-700" data-id="element-2082">
                  Amendment Documents
                </label>
                <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md" data-id="element-2083">
                  <div className="space-y-1 text-center" data-id="element-2084">
                    <svg className="mx-auto h-12 w-12 text-gray-400" stroke="currentColor" fill="none" viewBox="0 0 48 48" aria-hidden="true" data-id="element-2085">
                      <path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" data-id="element-2086" />
                    </svg>
                    <div className="flex text-sm text-gray-600" data-id="element-2087">
                      <label htmlFor="file-upload" className="relative cursor-pointer bg-white rounded-md font-medium text-indigo-600 hover:text-indigo-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-indigo-500" data-id="element-2088">
                        <span data-id="element-2089">Upload files</span>
                        <input id="file-upload" name="file-upload" type="file" className="sr-only" multiple data-id="element-2090" />
                      </label>
                      <p className="pl-1" data-id="element-2091">or drag and drop</p>
                    </div>
                    <p className="text-xs text-gray-500" data-id="element-2092">
                      PDF, DOC, DOCX up to 10MB
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div className="pt-5 mt-6 border-t border-gray-200" data-id="element-2093">
              <div className="flex justify-end" data-id="element-2094">
                <button type="button" onClick={() => setShowForm(false)} className="bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none" data-id="element-2095">
                  Cancel
                </button>
                <button type="submit" className="ml-3 inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none" data-id="element-2096">
                  {selectedAmendment ? 'Update Amendment' : 'Create Amendment'}
                </button>
              </div>
            </div>
          </form>
        </div> : <>
          {/* Search */}
          <div className="mb-6" data-id="element-2097">
            <div className="max-w-md" data-id="element-2098">
              <div className="relative rounded-md shadow-sm" data-id="element-2099">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-2100">
                  <SearchIcon className="h-5 w-5 text-gray-400" data-id="element-2101" />
                </div>
                <input type="text" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 sm:text-sm border-gray-300 rounded-md" placeholder="Search amendments..." data-id="element-2102" />
              </div>
            </div>
          </div>
          {/* Amendments Table */}
          <div className="bg-white shadow overflow-hidden sm:rounded-lg" data-id="element-2103">
            <div className="overflow-x-auto" data-id="element-2104">
              <table className="min-w-full divide-y divide-gray-200" data-id="element-2105">
                <thead className="bg-gray-50" data-id="element-2106">
                  <tr data-id="element-2107">
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2108">
                      Amendment
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2109">
                      Contract
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2110">
                      Details
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2111">
                      Dates
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2112">
                      Status
                    </th>
                    <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2113">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200" data-id="element-2114">
                  {filteredAmendments.length === 0 ? <tr data-id="element-2115">
                      <td colSpan={6} className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-center" data-id="element-2116">
                        No amendments found matching your criteria.
                      </td>
                    </tr> : filteredAmendments.map(amendment => {
                const StatusIcon = getStatusIcon(amendment.status);
                return <tr key={amendment.id} className="hover:bg-gray-50" data-id="element-2117">
                          <td className="px-6 py-4 whitespace-nowrap" data-id="element-2118">
                            <div className="flex items-center" data-id="element-2119">
                              <div data-id="element-2120">
                                <div className="text-sm font-medium text-indigo-600" data-id="element-2121">
                                  {amendment.id}
                                </div>
                                <div className="text-sm text-gray-500" data-id="element-2122">
                                  Amendment #{amendment.amendmentNumber}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap" data-id="element-2123">
                            <div className="text-sm text-gray-900" data-id="element-2124">
                              {amendment.contractId}
                            </div>
                            <div className="text-sm text-gray-500" data-id="element-2125">
                              {amendment.contractName}
                            </div>
                            <div className="text-xs text-gray-500" data-id="element-2126">
                              {amendment.vendor}
                            </div>
                          </td>
                          <td className="px-6 py-4" data-id="element-2127">
                            <div className="text-sm font-medium text-gray-900 max-w-xs truncate" data-id="element-2128">
                              {amendment.title}
                            </div>
                            <div className="text-sm text-gray-500 max-w-xs truncate" data-id="element-2129">
                              {amendment.description}
                            </div>
                            <div className="text-xs text-gray-500 max-w-xs truncate" data-id="element-2130">
                              Reason: {amendment.reason}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-2131">
                            <div className="flex items-center" data-id="element-2132">
                              <CalendarIcon className="h-4 w-4 mr-1 text-gray-400" data-id="element-2133" />
                              <span data-id="element-2134">Requested: {amendment.requestDate}</span>
                            </div>
                            {amendment.approvalDate && <div className="flex items-center mt-1" data-id="element-2135">
                                <CalendarIcon className="h-4 w-4 mr-1 text-gray-400" data-id="element-2136" />
                                <span data-id="element-2137">Approved: {amendment.approvalDate}</span>
                              </div>}
                            {amendment.effectiveDate && <div className="flex items-center mt-1" data-id="element-2138">
                                <CalendarIcon className="h-4 w-4 mr-1 text-gray-400" data-id="element-2139" />
                                <span data-id="element-2140">
                                  Effective: {amendment.effectiveDate}
                                </span>
                              </div>}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap" data-id="element-2141">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(amendment.status)}`} data-id="element-2142">
                              <StatusIcon className="h-3 w-3 mr-1" data-id="element-2143" />
                              {amendment.status}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-2144">
                            <div className="flex justify-end space-x-2" data-id="element-2145">
                              <button onClick={() => handleEdit(amendment)} className="text-indigo-600 hover:text-indigo-900" data-id="element-2146">
                                Edit
                              </button>
                              <button className="text-blue-600 hover:text-blue-900" title="View Document" data-id="element-2147">
                                <FileIcon className="h-4 w-4" data-id="element-2148" />
                              </button>
                            </div>
                          </td>
                        </tr>;
              })}
                </tbody>
              </table>
            </div>
          </div>
        </>}
    </div>;
}